/**
 * The frames drawn over a homework page (#661): one rounded frame in page coordinates around each block, with its mark at the corner
 * (✓ ✗ ⚑ ○ ◌) and its label chip on the top edge. The questions of a page are not drawn over it: they are `QuestionBand`, above the page (#673).
 *
 * - **Never colour alone.** The colour of a frame is a token per kind, and every frame also carries a label and, unless it was only read on a phone, its mark; a ✓ or ✗
 *   is only ever what the response's own mark says (`feedback-blocks.ts`), and a block with no verdict is neutral.
 * - **In the page's own pixels.** A frame is placed by the server's numbers as shares of the page image (`left`, `top` and the like are
 *   physical: the page is a picture and is never mirrored), so it moves and scales with the page. The corner mark is a button, so a frame
 *   can be picked with the keyboard; a tap on the frame itself is the viewer's (`data-frame-id`).
 * - Nothing here asks for or records anything.
 */

import { useFeedbackLabels } from '../../i18n/homework-feedback-labels.js';
import { useMessages } from '../../i18n/locale.js';
import { feedbackChrome, markGlyph } from './feedback-chrome.js';
import { onlyRead, questionGroup, questionMark, stripStarts, type BoardKind, type BoardNote } from './feedback-blocks.js';
import { UntrustedText } from './untrusted-text.js';

/** The mark at a frame's corner. The word is what is read out (the button's name), never this. */
export function kindGlyph(note: Pick<BoardNote, 'kind' | 'mark'>): string {
  const glyphs: Record<BoardKind, string> = { right: '✓', wrong: '✗', look: '⚑', unchecked: '○', review: '◌' };
  return note.mark === 'under_review' ? markGlyph('under_review') : glyphs[note.kind];
}

/**
 * How a frame's outline is drawn (#406): every frame is dashed, because a frame is an area and not an exact position, and no frame is solid.
 * Look-here's dashes are longer (a heavier stroke), so a pointer is told apart from a check by more than its colour.
 */
export function frameOutline(kind: BoardKind | 'line'): 'dashed' | 'dashed-bold' {
  return kind === 'look' ? 'dashed-bold' : 'dashed';
}

export interface FramesProps {
  readonly widthPx: number;
  readonly heightPx: number;
  /** The notes of this page that have a frame. */
  readonly notes: readonly BoardNote[];
  readonly selectedId: string | undefined;
  readonly onSelect: (id: string) => void;
  /** The corner mark also carries the note's number (the phone's whole-page view, where the note is a sheet). */
  readonly showNumbers?: boolean;
}

export function useNoteWords(): (note: BoardNote) => { readonly name: string; readonly tag: React.ReactNode } {
  const t = useFeedbackLabels();
  const m = useMessages(feedbackChrome);
  return (note) => {
    if (note.mark === 'check_this') return { name: t.channelFlagDefault, tag: note.questionLabel === null ? t.channelFlagDefault : <UntrustedText text={note.questionLabel} /> };
    const name = t.markWord(note.mark);
    if (note.kind === 'unchecked' && note.mark === 'read_not_checked') return { name, tag: m.tagNotChecked };
    return { name, tag: note.questionLabel === null ? name : <UntrustedText text={note.questionLabel} /> };
  };
}

/**
 * The questions that start on a page, as one grey band above it (#673): bold "Question 2", then the question's own words when the response carries
 * them. It is never drawn over the page, so it cannot cover handwriting. No question label on the page, no band.
 */
export function QuestionBand({ notes }: { readonly notes: readonly BoardNote[] }): React.JSX.Element | null {
  const m = useMessages(feedbackChrome);
  const starts = stripStarts(notes, true);
  const rows = notes.filter((note) => starts.has(note.id) && note.questionLabel !== null);
  if (rows.length === 0) return null;
  return (
    <ul className="fb-qband">
      {rows.map((note) => (
        <li key={note.id} className="fb-qband-row">
          <span className="fb-strip-label">
            {m.questionStrip(questionGroup(note.questionLabel) ?? note.questionLabel ?? '')}
            {m.questionMark(questionMark(notes, note.questionLabel))}
          </span>
          {note.questionText === undefined ? null : <span dir="auto" className="fb-strip-text"><UntrustedText text={note.questionText} /></span>}
        </li>
      ))}
    </ul>
  );
}

export function PageFrames({ widthPx, heightPx, notes, selectedId, onSelect, showNumbers = false }: FramesProps): React.JSX.Element {
  const m = useMessages(feedbackChrome);
  const wordsOf = useNoteWords();
  let pinned = 0;
  return (
    <div className="fb-frames">
      {notes.map((note) => {
        const box = note.blockBbox;
        if (box === null) return null;
        const { name, tag } = wordsOf(note);
        // The phone's whole page, as the board draws it: pins count from 1 on each page, and a line that was only read has its frame and label and no pin.
        const hasPin = !showNumbers || !onlyRead(note);
        if (hasPin) pinned += 1;
        const pinNumber = showNumbers ? pinned : note.number;
        return (
          <div
            key={note.id}
            className="fb-frame"
            data-kind={note.kind}
            data-outline={frameOutline(note.kind)}
            data-selected={note.id === selectedId}
            data-frame-id={note.id}
            style={{
              left: `${String((box.x0 / widthPx) * 100)}%`,
              top: `${String((box.y0 / heightPx) * 100)}%`,
              width: `${String(((box.x1 - box.x0) / widthPx) * 100)}%`,
              height: `${String(((box.y1 - box.y0) / heightPx) * 100)}%`,
            }}
          >
            <span className="fb-frame-tag">{tag}</span>
            {hasPin ? (
              <button
                type="button"
                className="fb-frame-badge"
                data-hit
                data-numbered={showNumbers}
                aria-label={m.pin(pinNumber, name, false)}
                aria-pressed={note.id === selectedId}
                onClick={() => onSelect(note.id)}
              >
                <span className="fb-badge-dot" aria-hidden="true">
                  {kindGlyph(note)}
                  {showNumbers ? <span className="fb-badge-number">{pinNumber}</span> : null}
                </span>
              </button>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}
